import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Calendar, Clock, Users, Phone, User, Check, Mail, Home, Sun } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { useLanguage } from "@/contexts/LanguageContext";

const Booking = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    guests: "",
    date: "",
    time: "",
    message: "",
    seating: "" as "" | "indoor" | "outdoor",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const validateField = (name: string, value: string) => {
    let error = "";
    
    switch (name) {
      case "firstName":
      case "lastName":
        if (!value.trim()) error = "Detta fält är obligatoriskt";
        else if (value.length < 2) error = "Minst 2 tecken krävs";
        break;
      case "email":
        if (!value.trim()) error = "E-post krävs";
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) error = "Ogiltig e-postadress";
        else if (value.length > 255) error = "E-postadressen är för lång";
        break;
      case "phone":
        if (!value.trim()) error = "Telefonnummer krävs";
        else if (!/^[\d\s\-+()]{8,}$/.test(value)) error = "Ogiltigt telefonnummer";
        break;
      case "guests":
        if (!value) error = "Antal gäster krävs";
        else if (parseInt(value) < 1) error = "Minst 1 gäst";
        else if (parseInt(value) > 20) error = "Max 20 gäster";
        break;
      case "date":
        if (!value) error = "Datum krävs";
        break;
      case "time":
        if (!value) error = "Tid krävs";
        else if (value < "16:00") error = "Tidigast kl 16:00";
        else if (value > "24:00") error = "Senast kl 24:00";
        break;
      case "seating":
        if (value !== "indoor" && value !== "outdoor") error = "Välj inomhus eller uteservering";
        break;
    }
    
    return error;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    setFormData({
      ...formData,
      [name]: value
    });

    // Real-time validation
    const error = validateField(name, value);
    setErrors(prev => ({
      ...prev,
      [name]: error
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Validate all fields
    const newErrors: Record<string, string> = {};
    Object.keys(formData).forEach((key) => {
      if (key !== "message") {
        const error = validateField(key, formData[key as keyof typeof formData]);
        if (error) newErrors[key] = error;
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast({
        title: "Vänligen korrigera felen i formuläret",
        variant: "destructive",
      });
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-booking-email`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "apikey": import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          },
          body: JSON.stringify({
            ...Object.fromEntries(
              Object.entries(formData).map(([k, v]) => [k, typeof v === 'string' ? v.trim() : v])
            ),
            guests: parseInt(formData.guests, 10),
              seating: formData.seating,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Något gick fel");
      }

      // Show success animation
      setShowSuccess(true);

      toast({
        title: "Bokningsförfrågan mottagen!",
        description:
          "Vi kommer att kontakta dig inom kort för att bekräfta din bokning. Får du inget bekräftelsemejl? Kontrollera din skräppost och markera mejlet som 'inte skräp' så hamnar vi i inboxen nästa gång.",
      });
    } catch (error: any) {
      toast({
        title: "Något gick fel",
        description: error.message || "Försök igen senare eller ring oss direkt.",
        variant: "destructive",
      });
      setIsSubmitting(false);
      return;
    }

    // Reset form after animation
    setTimeout(() => {
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        guests: "",
        date: "",
        time: "",
        message: "",
        seating: "",
      });
      setErrors({});
      setShowSuccess(false);
      setIsSubmitting(false);
    }, 2000);
  };

  return (
    <section id="booking" className="section-padding bg-gradient-to-b from-background to-background/50">
      <div className="container-custom">
        <div className="text-center mb-12 animate-fade-in-up">
          <h2 className="heading-luxury text-4xl md:text-5xl mb-4 text-foreground text-gradient">{t('booking.heading')}</h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            {t('booking.description')}
          </p>
        </div>

        <Card className="max-w-3xl mx-auto bg-card/50 backdrop-blur border-gold/20 shadow-luxury">
          <CardContent className="p-4 sm:p-6 lg:p-8">
            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
              {/* Name Fields */}
              <div className="grid sm:grid-cols-2 gap-4 sm:gap-6">
                <div className="space-y-2">
                  <Label htmlFor="firstName" className="text-foreground flex items-center gap-2">
                    <User className="w-4 h-4 text-gold" />
                    {t('booking.firstName')} *
                  </Label>
                  <Input
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    placeholder={t('booking.firstNamePlaceholder')}
                    required
                    className={`border-gold/30 focus:border-gold transition-all ${
                      errors.firstName ? "border-destructive focus:border-destructive" : ""
                    } ${formData.firstName && !errors.firstName ? "border-green-500" : ""}`}
                  />
                  {errors.firstName && (
                    <p className="text-sm text-destructive mt-1">{errors.firstName}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName" className="text-foreground flex items-center gap-2">
                    <User className="w-4 h-4 text-gold" />
                    {t('booking.lastName')} *
                  </Label>
                  <Input
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    placeholder={t('booking.lastNamePlaceholder')}
                    required
                    className={`border-gold/30 focus:border-gold transition-all ${
                      errors.lastName ? "border-destructive focus:border-destructive" : ""
                    } ${formData.lastName && !errors.lastName ? "border-green-500" : ""}`}
                  />
                  {errors.lastName && (
                    <p className="text-sm text-destructive mt-1">{errors.lastName}</p>
                  )}
                </div>
              </div>

              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-foreground flex items-center gap-2">
                  <Mail className="w-4 h-4 text-gold" />
                  {t('booking.email')} *
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder={t('booking.emailPlaceholder')}
                  required
                  className={`border-gold/30 focus:border-gold transition-all ${
                    errors.email ? "border-destructive focus:border-destructive" : ""
                  } ${formData.email && !errors.email ? "border-green-500" : ""}`}
                />
                {errors.email && (
                  <p className="text-sm text-destructive mt-1">{errors.email}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone" className="text-foreground flex items-center gap-2">
                  <Phone className="w-4 h-4 text-gold" />
                  {t('booking.phone')} *
                </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder={t('booking.phonePlaceholder')}
                    required
                    className={`border-gold/30 focus:border-gold transition-all ${
                      errors.phone ? "border-destructive focus:border-destructive" : ""
                    } ${formData.phone && !errors.phone ? "border-green-500" : ""}`}
                  />
                  {errors.phone && (
                    <p className="text-sm text-destructive mt-1">{errors.phone}</p>
                  )}
              </div>

              {/* Guests, Date, Time */}
              <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
                <div className="space-y-2">
                  <Label htmlFor="guests" className="text-foreground flex items-center gap-2">
                    <Users className="w-4 h-4 text-gold" />
                    {t('booking.guests')} *
                  </Label>
                  <Input
                    id="guests"
                    name="guests"
                    type="number"
                    min="1"
                    max="20"
                    value={formData.guests}
                    onChange={handleChange}
                    placeholder={t('booking.guestsPlaceholder')}
                    required
                    className={`border-gold/30 focus:border-gold transition-all ${
                      errors.guests ? "border-destructive focus:border-destructive" : ""
                    } ${formData.guests && !errors.guests ? "border-green-500" : ""}`}
                  />
                  {errors.guests && (
                    <p className="text-sm text-destructive mt-1">{errors.guests}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="date" className="text-foreground flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-gold" />
                    {t('booking.date')} *
                  </Label>
                  <Input
                    id="date"
                    name="date"
                    type="date"
                    value={formData.date}
                    onChange={handleChange}
                    required
                    min={new Date().toISOString().split('T')[0]}
                    className={`border-gold/30 focus:border-gold transition-all ${
                      errors.date ? "border-destructive focus:border-destructive" : ""
                    } ${formData.date && !errors.date ? "border-green-500" : ""}`}
                  />
                  {errors.date && (
                    <p className="text-sm text-destructive mt-1">{errors.date}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="time" className="text-foreground flex items-center gap-2">
                    <Clock className="w-4 h-4 text-gold" />
                    {t('booking.time')} *
                  </Label>
                  <Input
                    id="time"
                    name="time"
                    type="time"
                    min="16:00"
                    max="24:00"
                    value={formData.time}
                    onChange={handleChange}
                    required
                    className={`border-gold/30 focus:border-gold transition-all ${
                      errors.time ? "border-destructive focus:border-destructive" : ""
                    } ${formData.time && !errors.time ? "border-green-500" : ""}`}
                  />
                  {errors.time && (
                    <p className="text-sm text-destructive mt-1">{errors.time}</p>
                  )}
                </div>
              </div>

              {/* Message */}
              <div className="space-y-2">
                <Label htmlFor="message" className="text-foreground">
                  {t('booking.message')}
                </Label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder={t('booking.messagePlaceholder')}
                  rows={4}
                  maxLength={1000}
                  className="flex w-full rounded-md border border-gold/30 bg-background px-3 py-2 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                />
                {formData.message.length > 900 && (
                  <p className="text-xs text-muted-foreground text-right">{formData.message.length}/1000</p>
                )}
              </div>

              {/* Seating: Indoor / Outdoor */}
              <div className="space-y-2">
                <Label className="text-foreground flex items-center gap-2">
                  <Home className="w-4 h-4 text-gold" />
                  {t('booking.seating')} *
                </Label>
                <div className="grid grid-cols-2 gap-3 sm:gap-4">
                  {([
                    { value: "indoor", icon: Home, label: t('booking.seatingIndoor') },
                    { value: "outdoor", icon: Sun, label: t('booking.seatingOutdoor') },
                  ] as const).map(({ value, icon: Icon, label }) => {
                    const selected = formData.seating === value;
                    return (
                      <button
                        key={value}
                        type="button"
                        onClick={() => {
                          setFormData((prev) => ({ ...prev, seating: value }));
                          setErrors((prev) => ({ ...prev, seating: "" }));
                        }}
                        aria-pressed={selected}
                        className={`group relative flex flex-col items-center justify-center gap-2 rounded-md border px-4 py-5 transition-all backdrop-blur ${
                          selected
                            ? "border-gold bg-gold/10 text-gold shadow-gold"
                            : "border-gold/30 bg-background/40 text-foreground hover:border-gold/60 hover:bg-gold/5"
                        } ${errors.seating && !selected ? "border-destructive/60" : ""}`}
                        style={selected ? { boxShadow: 'var(--shadow-gold-glow)' } : undefined}
                      >
                        <Icon className={`w-6 h-6 ${selected ? "text-gold" : "text-gold/70"}`} />
                        <span className="text-sm font-semibold tracking-wide">{label}</span>
                        {selected && (
                          <Check className="absolute top-2 right-2 w-4 h-4 text-gold" />
                        )}
                      </button>
                    );
                  })}
                </div>
                {errors.seating && (
                  <p className="text-sm text-destructive mt-1">{errors.seating}</p>
                )}
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                size="lg"
                disabled={isSubmitting || showSuccess || !formData.seating}
                className="w-full bg-gold hover:bg-gold-light text-background font-semibold text-lg py-6 shadow-gold transition-all transform hover:scale-105 relative overflow-hidden"
                style={{ boxShadow: 'var(--shadow-gold-glow)' }}
              >
                {showSuccess ? (
                  <span className="flex items-center gap-2 animate-fade-in">
                    <Check size={24} />
                    {t('booking.booked')}
                  </span>
                ) : isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin">⏳</span>
                    {t('booking.submitting')}
                  </span>
                ) : (
                  t('booking.submit')
                )}
              </Button>

              <p className="text-sm text-muted-foreground text-center">
                {t('booking.required')}
              </p>
            </form>
          </CardContent>
        </Card>

        {/* Info */}
        <div className="text-center mt-8 text-muted-foreground">
          <p>{t('booking.callUs')} <a href="tel:040470060" className="text-gold hover:text-gold-light">040 47 00 60</a></p>
        </div>
      </div>
    </section>
  );
};

export default Booking;
